import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, FolderCode, Users, LogOut, Settings } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const LOGO_URL = "/absolin-logo.png";

const NavItem = ({ to, icon: Icon, label, active }) => (
  <Link
    to={to}
    data-testid={`sidebar-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
    className={cn(
      "sidebar-nav-item flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium",
      active
        ? "bg-blue-50 text-blue-700"
        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
    )}
  >
    <Icon className="w-4 h-4 flex-shrink-0" />
    <span>{label}</span>
  </Link>
);

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navItems = [
    { to: "/", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/repository", icon: FolderCode, label: "Repository" },
  ];

  if (user?.role === "admin") {
    navItems.push({ to: "/users", icon: Users, label: "Users" });
    navItems.push({ to: "/settings", icon: Settings, label: "Settings" });
  }

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="w-60 bg-white border-r border-slate-200 flex flex-col flex-shrink-0" data-testid="sidebar">
        <div className="p-4 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-3 group" data-testid="sidebar-brand">
            <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 bg-white">
              <img src={LOGO_URL} alt="Absolin" className="w-full h-full object-contain" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] text-blue-700 uppercase tracking-widest font-bold">SQL Repository</div>
              <div className="text-[10px] text-slate-400 mt-0.5">Infinite Digital Possibilities</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 px-3 py-2">Navigate</div>
          {navItems.map((item) => (
            <NavItem
              key={item.to}
              to={item.to}
              icon={item.icon}
              label={item.label}
              active={location.pathname === item.to}
            />
          ))}
        </nav>

        <div className="p-3 border-t border-slate-200">
          <div className="px-3 py-2 mb-2">
            <div className="text-xs font-medium text-slate-900 truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-[11px] text-slate-500 truncate font-mono">{user?.email}</div>
            <div className="mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600">
              {user?.role}
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            size="sm"
            className="w-full justify-start text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            data-testid="sidebar-logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign out
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">{children}</main>
    </div>
  );
}
